import axios from 'axios';

import store from '@/bootstrap/vuex';
import config from '@/config/axios';
import router from '@/bootstrap/router';
import i18n from '@/bootstrap/i18n';
import session from '@/storage/session';
import { createApp } from 'vue';
import user from '@/plugins/user';
import PLAYGROUND_SERVER_ERROR_KEYS, { LOCALLY_SURFACED_KEYS } from '@/utilities/playground-server-errors';

const { t } = i18n.global;
window.$t = t;

// TODO - refactor main so that we can use the vue instance to access the user plugin
// instantiate a vue instance to access plugin methods
let app = createApp();
app.use(user);

const http = axios.create(config);

/**
 * The HeadOffice API base URL resolved from the central axios config. Exported
 * so non-axios callers (e.g. `fetch`-based SSE streaming) build request URLs
 * against the same base as every other API call.
 *
 * @type {string}
 */
export const apiBaseUrl = config.baseURL ?? '';

http.defaults.withCredentials = true;
axios.defaults.withXSRFToken = true;
http.defaults.headers.common['X-Requested-With'] = 'XMLHttpRequest';

const loadedBuildHash = document.querySelector('meta[name="app-build-hash"]')?.content;

/**
 * Read a browser cookie value by name. Returns `null` when missing.
 *
 * @param {string} name
 * @returns {string|null}
 */
function readCookie(name) {
    const prefix = `${encodeURIComponent(name)}=`;
    const found = document.cookie
        .split(';')
        .map((segment) => segment.trim())
        .find((segment) => segment.startsWith(prefix));
    return found ? decodeURIComponent(found.slice(prefix.length)) : null;
}

/**
 * Build the HeadOffice API auth/context headers (X-WEBAPP, X-Organisation,
 * X-Organisation-Id). Shared by the axios request interceptor and by callers
 * that bypass axios (e.g. `fetch`-based SSE streaming).
 *
 * @returns {Record<string, string>}
 */
export function buildAuthHeaders() {
    const headers = {
        'X-WEBAPP': 'true',
    };

    const organisationSlug = router.currentRoute.value.params.organisation;
    if (organisationSlug !== undefined) {
        headers['X-Organisation'] = organisationSlug;
    }

    const organisation = store.state.organisation[organisationSlug];
    if (organisation) {
        headers['X-Organisation-Id'] = organisation.id;
    }

    return headers;
}

/**
 * Build the headers needed to authenticate a non-axios request against the
 * HeadOffice API. Extends `buildAuthHeaders()` with `X-Requested-With` and the
 * XSRF token (which axios attaches natively via `withXSRFToken`).
 *
 * @returns {Record<string, string>}
 */
export function buildFetchAuthHeaders() {
    const headers = {
        ...buildAuthHeaders(),
        'X-Requested-With': 'XMLHttpRequest',
    };

    const xsrfToken = readCookie('XSRF-TOKEN');
    if (xsrfToken) {
        headers['X-XSRF-TOKEN'] = xsrfToken;
    }

    return headers;
}

// Add a request interceptor
http.interceptors.request.use(
    (config) => {
        // AppState.stillLoading++
        // store.dispatch('app/increaseOutstandingRequests')

        let headers = config.headers || (config.headers = {});

        // if (window.token !== null && window.token !== "undefined") {
        //     headers.Authorization = "Bearer " + window.token;
        // }

        // console.log('org::axios', store.state.organisation[router.currentRoute.value.params.organisation]);

        Object.assign(headers, buildAuthHeaders());

        return config;
    },
    (error) => {
        // Do something with request error
        return Promise.reject(error);
    }
);

// Add a response interceptor
http.interceptors.response.use(
    (response) => {
        const serverBuildHash = response.headers?.['x-app-build-hash'];
        if (loadedBuildHash && serverBuildHash && serverBuildHash !== loadedBuildHash && !store.state.app.newVersionAvailable) {
            store.commit('app/setNewVersionAvailable');
        }

        // Do something with response data
        if (response.status === 200) {
            if (response.config['cache-response'] !== undefined && response.config['cache-response'] == true) {
                session.store(response.config['cache-key'], response.data);
            }
        }
        // AppState.stillLoading--
        // store.dispatch('app/decreaseOutstandingRequests', null, { root:true })

        return response.data;
    },
    (error) => {
        error.notHandled = true;
        // store.dispatch('app/decreaseOutstandingRequests', null, { root:true })

        if (error.response.status === 400) {
            error.notHandled = false;
            window.flashMessage({
                type: 'error',
                message: t('error.400.message'),
            });
        }

        if (error.response.status === 402) {
            error.notHandled = false;

            router
                .push({
                    name: 'settings.subscription-invoices.billing',
                })
                .catch((err) => {
                    if (err.name != 'NavigationDuplicated') {
                        throw err;
                    }
                });
        }

        if (error.response.status === 423 && error.response.data.message === 'books.closed') {
            error.notHandled = false;
            window.flashMessage({
                type: 'error',
                message: t('validation.after_close_date'),
            });
        }

        if (error.response.status === 401) {
            /*
             * The 401 sibling of the `handlesForbidden` opt-out below, and
             * per-request for the same reason: it has to happen before the
             * navigation is queued. The prospect intake page renders its own
             * sign-in state off a 401 (onboarding-04) — resetting the store and
             * bouncing to the app login would strand a signed-out prospect with
             * no way back to their token.
             */
            if (error.config && error.config.handlesUnauthenticated) {
                return Promise.reject(error);
            }

            error.notHandled = false;

            app.config.globalProperties.$user.reset();

            router
                .push({
                    name: 'guest.login',
                })
                .catch((err) => {
                    if (err.name != 'NavigationDuplicated') {
                        throw err;
                    }
                });
        }

        if (error.response.status && error.response.status === 403) {
            if (error.response.data && error.response.data.code === 'EMAIL_NOT_VERIFIED') {
                return Promise.reject(error);
            }

            // The caller has said it renders its own denied state. Leave the user
            // where they are and let the page explain itself.
            //
            // `notHandled` is NOT this opt-out despite appearing to be one: it is
            // set below, after the navigation is already queued, and every
            // consumer in the app reads it as "nobody flashed a message for you".
            // Opting out has to happen before the push, and has to be per-request,
            // which is why it rides on the request config.
            //
            // Checked BEFORE the playground map: an explicit per-request contract
            // beats a global mapping. No playground surface sets the flag today,
            // so neither feature's behaviour changes — but if one ever does, the
            // page that promised to explain itself is the one that should.
            if (error.config && error.config.handlesForbidden) {
                return Promise.reject(error);
            }

            // A playground refusal is actionable where the user stands — a
            // blocked capability (bank feeds, AI, invitations, …) must surface
            // its specific mapped message on the triggering screen, never the
            // generic forbidden page. Keys a playground surface renders inline
            // itself are only exempted from the toast, not from the redirect.
            const playgroundErrorKey = PLAYGROUND_SERVER_ERROR_KEYS[error.response.data && error.response.data.message];
            if (playgroundErrorKey) {
                if (!LOCALLY_SURFACED_KEYS.includes(error.response.data.message)) {
                    error.notHandled = false;
                    window.flashMessage({
                        type: 'error',
                        message: t(playgroundErrorKey),
                    });
                }

                return Promise.reject(error);
            }

            error.notHandled = false;

            /*
             * The organisation param is carried explicitly because
             * `errors.forbidden` lives at `/:organisation/errors/403`:
             * vue-router only reuses params from the CURRENT route, so a 403
             * arriving while the router sits somewhere without one (the
             * organisation chooser, or a still-unsettled initial navigation)
             * made this push throw `Missing required param "organisation"` and
             * navigate nowhere. `__my__` is the slug fallback the root
             * catch-all in `routes/shared/error.js` already uses.
             */
            router
                .push({
                    name: 'errors.forbidden',
                    params: { organisation: router.currentRoute.value.params.organisation ?? '__my__' },
                })
                .catch((err) => {
                    if (err.name != 'NavigationDuplicated') {
                        throw err;
                    }
                });
        }

        if (error.response.status === 419) {
            error.notHandled = false;

            app.config.globalProperties.$user.reset();

            router
                .push({
                    name: 'guest.login',
                })
                .catch((err) => {
                    if (err.name != 'NavigationDuplicated') {
                        throw err;
                    }
                });
        }

        if (error.response.status && error.response.status === 429) {
            error.notHandled = false;
            window.flashMessage({
                type: 'error',
                message: t('error.429.message'),
            });
        }

        /*
         * The 500 and 503 pushes carry the organisation param explicitly for
         * the same reason as the 403 above: their pages also live under
         * `/:organisation/errors/…`, so the bare named push threw and went
         * nowhere whenever the current route had no organisation param.
         */
        if (error.response.status === 500) {
            error.notHandled = false;
            router
                .push({
                    name: 'errors.server-error',
                    params: { organisation: router.currentRoute.value.params.organisation ?? '__my__' },
                })
                .catch((err) => {
                    if (err.name != 'NavigationDuplicated') {
                        throw err;
                    }
                });
        }

        if (error.response.status === 503) {
            error.notHandled = false;
            router
                .push({
                    name: 'errors.service-unavailable',
                    params: { organisation: router.currentRoute.value.params.organisation ?? '__my__' },
                })
                .catch((err) => {
                    if (err.name != 'NavigationDuplicated') {
                        throw err;
                    }
                });
        }

        return Promise.reject(error);
    }
);

export default http;
